// API client for Go backend
import type { IdMap, Transaction_t, SummaryLine_t, Trefwoord_t } from './types';

const API_BASE = '/api';

// Types for API responses
export interface SummaryResponse {
	summary: SummaryLine_t[];
	id2trefwoord: IdMap<string>;
	totaal: number;
	totaal_pm: number;
}

export interface TrefwoordenResponse {
	id2trefwoord: IdMap<string>;
	trefwoordIds: number[];
}

export interface TransactionsResponse {
	id2trefwoord: IdMap<string>;
	trefwoordIds: number[];
	transactions: Transaction_t[];
	totaal: number;
}

export interface LatestResponse {
	from: string;
	until: string;
}

// API functions
export async function getSummary(from: string, until: string): Promise<SummaryResponse> {
	const response = await fetch(`${API_BASE}/summary?from=${from}&until=${until}`);
	if (!response.ok) {
		throw new Error(`Failed to fetch summary: ${response.statusText}`);
	}
	return response.json();
}

export async function getTrefwoorden(): Promise<TrefwoordenResponse> {
	const response = await fetch(`${API_BASE}/trefwoord`);
	if (!response.ok) {
		throw new Error(`Failed to fetch trefwoorden: ${response.statusText}`);
	}
	return response.json();
}

export async function createTrefwoord(omschrijving: string): Promise<{ id: number }> {
	const response = await fetch(`${API_BASE}/trefwoord`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({ id: null, omschrijving })
	});

	if (!response.ok) {
		throw new Error(`Failed to create trefwoord: ${response.statusText}`);
	}
	return response.json();
}

export async function updateTrefwoord(id: number, omschrijving: string): Promise<{ id: number }> {
	const response = await fetch(`${API_BASE}/trefwoord/${id}`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({ omschrijving })
	});

	if (!response.ok) {
		throw new Error(`Failed to update trefwoord: ${response.statusText}`);
	}
	return response.json();
}

export async function deleteTrefwoord(id: number, moveToId: number): Promise<{ success: boolean }> {
	const response = await fetch(`${API_BASE}/trefwoord/${id}`, {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({ moveToId })
	});

	if (!response.ok) {
		throw new Error(`Failed to delete trefwoord: ${response.statusText}`);
	}
	return response.json();
}

export async function getTransactions(
	from: string,
	until: string,
	trefwoordId?: number
): Promise<TransactionsResponse> {
	let url = `${API_BASE}/transactions?from=${from}&until=${until}`;
	if (trefwoordId !== undefined) {
		url += `&trefwoordId=${trefwoordId}`;
	}

	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`Failed to fetch transactions: ${response.statusText}`);
	}
	return response.json();
}

export async function searchTransactions(
	from: string,
	until: string,
	search: string
): Promise<TransactionsResponse> {
	const response = await fetch(
		`${API_BASE}/search?from=${from}&until=${until}&q=${encodeURIComponent(search)}`
	);
	if (!response.ok) {
		throw new Error(`Failed to search transactions: ${response.statusText}`);
	}
	return response.json();
}

export async function changeTransactionTrefwoord(
	transId: number,
	trefwId: number,
	applyTo: string
): Promise<{ success: boolean }> {
	const response = await fetch(`${API_BASE}/transactions/change-trefwoord`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({ transId, twId: trefwId, applyTo })
	});

	if (!response.ok) {
		throw new Error(`Failed to change transaction trefwoord: ${response.statusText}`);
	}
	return response.json();
}

export async function multiChangeTransactionTrefwoord(
	trefwId: number,
	transIds: number[]
): Promise<{ success: boolean }> {
	const response = await fetch(`${API_BASE}/transactions/multi-change-trefwoord`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({ twId: trefwId, transIds })
	});

	if (!response.ok) {
		throw new Error(`Failed to change multiple transactions: ${response.statusText}`);
	}
	return response.json();
}

export async function getLatestDateRange(): Promise<LatestResponse> {
	const response = await fetch(`${API_BASE}/latest`);
	if (!response.ok) {
		throw new Error(`Failed to fetch latest date range: ${response.statusText}`);
	}
	return response.json();
}
